import { FeatherModule } from 'angular-feather';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  HttpXsrfTokenExtractor
} from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GraphQLModule } from './graphql.module';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { allIcons } from 'angular-feather/icons';
import { RegisterModule } from './layouts/register/register.module';
import { LoginModule } from './layouts/login/login.module';
import { RouterModule } from '@angular/router';
import {
  LangChangeEvent,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';

import { AuthInterceptor } from './config/auth-interceptor';
import { LocaleInterceptor } from './config/locale-interceptor';
import { forkJoin, map, Observable } from 'rxjs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { JuliaAgentModule } from './modules/julia-agent/julia-agent.module';
import {provideEcharts} from "ngx-echarts";
import { environment } from '../environments/environment';
import { XsrfInterceptor } from './config/xsrf.interceptor';

export class MultiTranslateHttpLoader implements TranslateLoader {
  constructor(
    private http: HttpClient,
    private resources: { prefix: string; suffix: string }[],
  ) {}

  getTranslation(lang: string): Observable<any> {
    const requests = this.resources.map((resource) =>
      this.http.get(`${resource.prefix}${lang}${resource.suffix}`),
    );

    return forkJoin(requests).pipe(
      map((responses) =>
        responses.reduce((acc, curr) => ({ ...acc, ...curr }), {}),
      ),
    );
  }
}

export function createTranslateLoader(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    { prefix: './assets/i18n/', suffix: '.json' },
    { prefix: './assets/i18n/invitations/', suffix: '.json' },
    { prefix: './assets/i18n/deal-management/', suffix: '.json' },
    { prefix: './assets/i18n/request/', suffix: '.json' },
    { prefix: './assets/i18n/chat/', suffix: '.json' },
    { prefix: './assets/i18n/julia-agent/', suffix: '.json' },
    { prefix: './assets/i18n/communs/upload-file/', suffix: '.json' },
  ]);
}

@NgModule({
  declarations: [AppComponent],

  imports: [
    BrowserAnimationsModule,
    MatDatepickerModule,
    MatInputModule,
    MatFormFieldModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    FormsModule,
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    GraphQLModule,
    NgbModule,

    RouterModule,
    JuliaAgentModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    FeatherModule.pick(allIcons),
    
  ],

  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: LocaleInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HttpXsrfTokenExtractor, useClass: XsrfInterceptor },    
    provideEcharts(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(public translationService: TranslateService) {
    const savedLang = localStorage.getItem('appLang');
    const currentLang =
      savedLang || this.translationService.currentLang || 'fr';

    this.translationService.use(currentLang);
    this.translationService.store.onLangChange.subscribe(
      (lang: LangChangeEvent) => {
        localStorage.setItem('appLang', lang.lang);
        this.translationService.use(lang.lang);
      },
    );
  }
}
